"use client";

import { useRef, useState } from "react";
import { Copy, Link as LinkIcon, MessageCircle, Share2, X } from "lucide-react";
import { Button } from "./ui/Button";
import type { RoomType } from "@/types";

export function ShareDesign({ roomLink, roomType }: { roomLink: string; roomType: RoomType }) {
  const roomName = roomType === "bedroom" ? "bedroom" : "living room";
  const captions = {
    classy: `Same room. A fresh perspective. ✨ Here's my ${roomName}, reimagined with Curated Den. What do you think?`,
    funky: `My ${roomName} just had its main-character moment. ✨ Take a peek at my Curated Den glow-up — room envy welcome!`,
  };
  const dialog = useRef<HTMLDialogElement>(null);
  const linkInput = useRef<HTMLInputElement>(null);
  const [tone, setTone] = useState<keyof typeof captions>("classy");
  const [message, setMessage] = useState(captions.classy);
  const [nativeShare, setNativeShare] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [status, setStatus] = useState("");
  const shareText = [message.trim(), roomLink].filter(Boolean).join("\n\n");

  const open = () => {
    setStatus("");
    setNativeShare(typeof navigator.share === "function");
    dialog.current?.showModal();
  };
  const copy = async (text: string, confirmation: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus(confirmation);
    } catch {
      linkInput.current?.focus();
      linkInput.current?.select();
      setStatus("Copy the selected room link below. You can copy your caption above too.");
    }
  };
  const share = async () => {
    if (sharing) return;
    if (!nativeShare) return copy(shareText, "Message and room link copied. Paste them into your favourite chat.");
    setSharing(true);
    setStatus("");
    try {
      await navigator.share({ title: `My ${roomName}, reimagined | Curated Den`, text: message.trim(), url: roomLink });
    } catch (error) {
      if (!(error instanceof Error && error.name === "AbortError")) {
        setStatus("Sharing couldn't open. Try WhatsApp or copy your room link below.");
      }
    } finally {
      setSharing(false);
    }
  };

  return <>
    <Button size="sm" onClick={open} aria-haspopup="dialog"><Share2 className="h-3.5 w-3.5" />Share my room</Button>
    <dialog ref={dialog} aria-labelledby="share-title" aria-describedby="share-description"
      onCancel={event => { if (sharing) event.preventDefault(); }}
      className="m-auto w-[calc(100%-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl border border-line bg-paper p-6 sm:p-8 text-ink shadow-xl backdrop:bg-ink/60 backdrop:backdrop-blur-sm">
      <button type="button" aria-label="Close sharing" disabled={sharing} onClick={() => dialog.current?.close()}
        className="absolute top-4 right-4 rounded-full bg-paper-deep p-2 text-ink-soft hover:text-ink disabled:opacity-50"><X className="h-5 w-5" /></button>
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-clay/10 text-clay"><Share2 className="h-6 w-6" /></div>
      <h2 id="share-title" className="text-center text-2xl font-bold">Share your room</h2>
      <p id="share-description" className="mt-2 text-center text-sm text-ink-soft">Good rooms deserve company. Invite a second opinion.</p>

      <fieldset disabled={sharing} className="mt-6">
        <legend className="mb-2 text-sm font-semibold">Pick your caption</legend>
        <div className="grid grid-cols-2 gap-2">
          {(["classy", "funky"] as const).map(value => <label key={value}
            className={`flex cursor-pointer items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm focus-within:ring-2 focus-within:ring-clay/40 ${tone === value ? "border-clay bg-clay/10 text-ink" : "border-line text-ink-soft"}`}>
            <input type="radio" name="share-tone" value={value} checked={tone === value} className="accent-clay"
              onChange={() => { setTone(value); setMessage(captions[value]); setStatus(""); }} />
            {value === "classy" ? "Classy" : "Funky"}
          </label>)}
        </div>
      </fieldset>
      <label htmlFor="share-caption" className="mb-2 mt-5 block text-sm font-semibold">Make it yours</label>
      <textarea id="share-caption" value={message} onChange={event => setMessage(event.target.value)} disabled={sharing}
        rows={4} maxLength={500} className="w-full rounded-xl border border-line bg-paper-deep p-3 text-sm focus:outline-none focus:ring-2 focus:ring-clay/40" />
      <p className="mt-2 text-xs leading-relaxed text-ink-soft">Anyone with this link can open your room, including the original photo and shopping list, and update its feedback.</p>

      <div className="mt-5 space-y-3">
        <Button className="w-full" onClick={share} disabled={sharing}>
          {nativeShare ? <Share2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {sharing ? "Opening share menu…" : nativeShare ? "Share with friends" : "Copy message & link"}
        </Button>
        <div className="grid grid-cols-2 gap-3">
          <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-line px-3 py-2.5 text-sm font-medium hover:bg-paper-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/50">
            <MessageCircle className="h-4 w-4" />WhatsApp
          </a>
          <Button variant="outline" onClick={() => copy(roomLink, "Room link copied. Ready to send to a friend.")} disabled={sharing}><LinkIcon className="h-4 w-4" />Copy link</Button>
        </div>
      </div>
      <label htmlFor="share-room-link" className="mb-2 mt-5 block text-xs font-semibold text-ink-soft">Room link</label>
      <input ref={linkInput} id="share-room-link" readOnly value={roomLink} onFocus={event => event.target.select()}
        className="w-full min-w-0 rounded-lg border border-line bg-paper-deep px-3 py-2 text-xs" />
      <p role="status" className="mt-3 text-sm text-ink-soft">{status}</p>
    </dialog>
  </>;
}
